// Turns one organisation accent colour into the accent tokens the apps need, in light and dark
// mode, with WCAG AA contrast guaranteed (spec section 2: "one colour, checked automatically for
// contrast in light and dark mode; replaces Circles Orange").

import { contrast, hexToOklch, mix, oklchToHex } from './color.ts';

export const AA_TEXT = 4.5;
export const AA_UI = 3;

/** Backgrounds the accent is checked against. Keep in sync with semantic/{light,dark}.json. */
export const SURFACES = {
  light: { background: '#FFFFFF', text: '#16181D' },
  dark: { background: '#101216', text: '#F2F3F5' },
} as const;

export interface AccentTokens {
  /** Highlights, unread badges, selection indicators. The brand colour as given. */
  highlight: string;
  /** Text and icons placed on `highlight`. */
  onHighlight: string;
  /** Buttons, links and active tab icons on the background (>= 4.5:1). */
  action: string;
  /** Text and icons on an `action` fill (>= 4.5:1). */
  onAction: string;
  /** Pressed state of `action`. */
  actionPressed: string;
  /** Quiet accent fill: selected rows, chips, the user's own chat bubbles. */
  subtle: string;
  /** Text on `subtle` in accent colour (>= 4.5:1). */
  onSubtle: string;
}

export interface AccentResult {
  light: AccentTokens;
  dark: AccentTokens;
  /** Human-readable notes when the given colour had to be adjusted. */
  adjustments: string[];
}

function bestOn(fill: string): string {
  const dark = SURFACES.light.text;
  const light = '#FFFFFF';
  return contrast(fill, dark) >= contrast(fill, light) ? dark : light;
}

/**
 * Move OKLCH lightness in `direction` until `color` reaches `target` contrast against every
 * colour in `against`. Hue is kept; chroma is reduced only where sRGB requires it.
 */
function adjustForContrast(
  color: string,
  against: string | string[],
  target: number,
  direction: -1 | 1,
): string {
  const list = Array.isArray(against) ? against : [against];
  const ok = (c: string) => list.every((bg) => contrast(c, bg) >= target);
  if (ok(color)) return color;
  const base = hexToOklch(color);
  for (let step = 1; step <= 200; step++) {
    const l = Math.min(1, Math.max(0, base.l + direction * step * 0.005));
    const candidate = oklchToHex({ ...base, l });
    if (ok(candidate)) return candidate;
  }
  return direction < 0 ? '#000000' : '#FFFFFF';
}

export interface AccentOverrides {
  /** Use this action colour in light mode instead of a derived one (it is still checked). */
  lightAction?: string;
}

export function deriveAccent(seed: string, overrides: AccentOverrides = {}): AccentResult {
  const adjustments: string[] = [];
  const highlight = oklchToHex(hexToOklch(seed));

  const modes = (['light', 'dark'] as const).map((mode) => {
    const { background } = SURFACES[mode];
    const direction = mode === 'light' ? -1 : 1;
    // Light mode: darker than the white background, labelled in white. Dark mode: lighter than
    // the dark background, labelled in dark text. Both pairs must reach 4.5:1.
    const label = mode === 'light' ? '#FFFFFF' : SURFACES.light.text;
    let action = adjustForContrast(highlight, [background, label], AA_TEXT, direction);
    if (mode === 'light' && overrides.lightAction) {
      action = adjustForContrast(overrides.lightAction, [background, label], AA_TEXT, -1);
    }
    if (action !== highlight) {
      adjustments.push(
        `${mode}: ${highlight} has ${contrast(highlight, background).toFixed(2)}:1 on ${background}; ` +
          `buttons and links use ${action} (${contrast(action, background).toFixed(2)}:1).`,
      );
    }
    const onAction = bestOn(action);
    const pressed = oklchToHex({
      ...hexToOklch(action),
      l: hexToOklch(action).l + (mode === 'light' ? -0.06 : 0.06),
    });
    const subtle = mix(background, highlight, mode === 'light' ? 0.16 : 0.24);
    const onSubtle = adjustForContrast(action, subtle, AA_TEXT, direction);
    return [
      mode,
      {
        highlight,
        onHighlight: bestOn(highlight),
        action,
        onAction,
        actionPressed: pressed,
        subtle,
        onSubtle,
      },
    ] as const;
  });

  return {
    light: modes[0]![1],
    dark: modes[1]![1],
    adjustments,
  };
}
